import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Check, AlertCircle, ArrowRight } from 'lucide-react';
import api from '../utils/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setResult({ success: false, message: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' });
      return;
    }
    if (password !== confirmPassword) {
      setResult({ success: false, message: 'كلمات المرور غير متطابقة' });
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password', { token, password });
      setResult({ success: true, message: res.data.message });
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.detail || 'حدث خطأ' });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" dir="rtl">
        <div className="bg-zinc-900 border border-red-500/30 rounded-xl p-8 max-w-sm w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-white text-lg font-bold mb-2">رابط غير صالح</h2>
          <p className="text-zinc-400 text-sm mb-6">لا يوجد توكن لإعادة التعيين</p>
          <button onClick={() => navigate('/login')} className="text-[#00E5FF] hover:underline text-sm">
            العودة لتسجيل الدخول
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
            <Lock className="w-7 h-7 text-[#00E5FF]" />
          </div>
          <h1 className="text-xl font-bold text-white">إعادة تعيين كلمة المرور</h1>
          <p className="text-zinc-500 text-sm mt-1">أدخل كلمة المرور الجديدة</p>
        </div>

        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6">
          {result?.success ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check className="w-7 h-7 text-emerald-400" />
              </div>
              <p className="text-emerald-400 font-bold">{result.message}</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                data-testid="go-login-btn"
              >
                <ArrowRight className="w-5 h-5" />
                تسجيل الدخول
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {result && !result.success && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm text-center" data-testid="reset-error">
                  {result.message}
                </div>
              )}
              <div>
                <label className="text-zinc-400 text-xs block mb-1.5">كلمة المرور الجديدة</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#00E5FF] focus:outline-none"
                  placeholder="6 أحرف على الأقل"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-xs block mb-1.5">تأكيد كلمة المرور</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#00E5FF] focus:outline-none"
                  placeholder="أعد إدخال كلمة المرور"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black font-bold rounded-xl transition-all disabled:opacity-50"
                data-testid="reset-submit-btn"
              >
                {loading ? 'جاري التغيير...' : 'تغيير كلمة المرور'}
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-4">
          <button onClick={() => navigate('/login')} className="text-zinc-500 hover:text-zinc-300 text-sm transition-colors">
            العودة لتسجيل الدخول
          </button>
        </div>
      </motion.div>
    </div>
  );
}
